import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Results.css';
import { useIntegration } from '../hooks/useIntegration';
import { filterNomenclature, countMatches } from '../utils/searchHelpers';

function formatPrice(value) {
  if (value === null || value === undefined) return null;
  return new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}

function StockOffers({ offers = [] }) {
  const [opened, setOpened] = useState(null);
  return (
    <div className="stock-offers">
      {offers.map((offer, idx) => (
        <div className="stock-offer-wrap" key={`${offer.source_id || offer.source}-${idx}`}>
          <button type="button" className="stock-offer-badge" onClick={() => setOpened(opened === idx ? null : idx)}>
            {offer.source}{offer.external_code ? ` · код ${offer.external_code}` : ''}: {offer.stock}
          </button>
          {opened === idx && (
            <div className="stock-price-popover">
              <b>{offer.source}</b>
              <div>{formatPrice(offer.price) ? `Цена: ${formatPrice(offer.price)} ₽` : 'Цена в прайсе не указана'}</div>
              {(offer.cards || []).length > 1 && (
                <div className="stock-card-list">
                  {offer.cards.map((card, cardIdx) => (
                    <div key={cardIdx}>
                      {card.external_code ? `Код ${card.external_code}` : 'Карточка без кода'}: {card.stock} шт.
                      {formatPrice(card.price) ? ` · ${formatPrice(card.price)} ₽` : ''}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function copyToClipboard(text, successMessage, onSuccess) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text)
      .then(() => { if (onSuccess) onSuccess(successMessage); })
      .catch(() => fallbackCopy(text, successMessage, onSuccess));
  } else {
    fallbackCopy(text, successMessage, onSuccess);
  }
}

function fallbackCopy(text, successMessage, onSuccess) {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  // Без явных top/left браузер сначала располагает элемент по месту в потоке
  // (в конце длинной страницы результатов), и .focus() ниже проматывает
  // страницу к нему, чтобы показать фокус — виден резкий "прыжок" вниз.
  // Пиним в верхний левый угол вьюпорта, чтобы он всегда был в зоне видимости
  // и focus() не вызывал скролл.
  textarea.style.top = '0';
  textarea.style.left = '0';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus({ preventScroll: true });
  textarea.select();
  try {
    document.execCommand('copy');
    if (onSuccess) onSuccess(successMessage);
  } catch (err) {
    if (onSuccess) onSuccess('Ошибка копирования. Скопируйте вручную.');
  }
  document.body.removeChild(textarea);
}

function Results({ token, orderId, onNavigate, userRole }) {
  const isAdmin = userRole === 'admin';
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, auto, manual
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editingQuantityId, setEditingQuantityId] = useState(null);
  const [editQuantityValue, setEditQuantityValue] = useState('');
  const [learningStates, setLearningStates] = useState({});
  const [rematchHintVisible, setRematchHintVisible] = useState(false);
  const [nomenclatureList, setNomenclatureList] = useState([]);
  const [companyHasCharacteristics, setCompanyHasCharacteristics] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [show1CModal, setShow1CModal] = useState(false);
  const [selectedForRematch, setSelectedForRematch] = useState(new Set());
  const [rematchComment, setRematchComment] = useState('');
  const [rematching, setRematching] = useState(false);
  const [rematchNotification, setRematchNotification] = useState(null); // {type: 'success'|'error', text: '...'}
  const [hasSnapshots, setHasSnapshots] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [showAddItems, setShowAddItems] = useState(false);
  const [addItemsText, setAddItemsText] = useState('');
  const [addingItems, setAddingItems] = useState(false);
  const [copyNotification, setCopyNotification] = useState(null);
  const [rawText, setRawText] = useState(null);
  const [showRawText, setShowRawText] = useState(false);
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [groupCatalog, setGroupCatalog] = useState(null);      // [{name, count}] | null (не загружено)
  const [groupCatalogError, setGroupCatalogError] = useState('');
  const [groupCatalogLoading, setGroupCatalogLoading] = useState(false);
  const [groupSelections, setGroupSelections] = useState({});  // {result_id: group_name}
  const [modalChecked, setModalChecked] = useState(new Set()); // result_ids, отмеченные в модалке для bulk

  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const lastCheckedIndexRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || '';
  const { settings } = useIntegration(token);

  useEffect(() => {
    if (orderId) {
      loadResults();
      loadNomenclature();
      loadHasCharacteristics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, token]);

  const loadResults = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await fetch(`${API_URL}/api/matching/results/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          window.location.reload();
          return;
        }
        throw new Error('Не удалось загрузить результаты');
      }

      const data = await response.json();
      const items = data.items || data; // backwards compat
      setResults(items);
      setRawText(data.raw_text || null);
      setHasSnapshots(!!data.has_snapshots);
      // Initialize learning states from server data
      const states = {};
      items.forEach(r => { states[r.id] = r.is_in_learning || false; });
      setLearningStates(states);
    } catch (err) {
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadNomenclature = async () => {
    try {
      const response = await fetch(`${API_URL}/api/nomenclature/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setNomenclatureList(data.map(item => item.name));
      }
    } catch (err) {
      // ignore
    }
  };

  const loadHasCharacteristics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/nomenclature/has-characteristics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCompanyHasCharacteristics(!!data.has_characteristics);
      }
    } catch (err) {
      // ignore
    }
  };

  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditValue('');
    setSearchTerm('');
    setHighlightedIndex(0);
  }, []);

  const handleEdit = (result) => {
    setEditingId(result.id);
    setEditValue(result.matched_name || '');
    setSearchTerm(result.matched_name || '');
    setHighlightedIndex(0);
  };

  // Закрытие dropdown при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Проверяем, что клик не по кнопке "Изменить" и не по matched-name
        if (!event.target.closest('.edit-btn') && !event.target.closest('.matched-name-clickable')) {
          handleCancelEdit();
        }
      }
    };

    if (editingId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingId, handleCancelEdit]);

  // Фокус на поле поиска при открытии dropdown
  useEffect(() => {
    if (editingId && searchInputRef.current) {
      searchInputRef.current.focus();
      searchInputRef.current.select();
    }
  }, [editingId]);

  const handleSaveEdit = async (resultId, selectedValue = null) => {
    const valueToSave = selectedValue || editValue;

    if (!valueToSave || !valueToSave.trim()) {
      alert('Выберите номенклатуру');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/matching/confirm/${resultId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ matched_name: valueToSave }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          window.location.reload();
          return;
        }
        throw new Error('Не удалось сохранить изменения');
      }

      // Update local state
      setResults(results.map(r =>
        r.id === resultId
          ? { ...r, matched_name: valueToSave, status: 'manual' }
          : r
      ));

      // Automatically save to learning (checked by default)
      setLearningStates(prev => ({ ...prev, [resultId]: true }));
      fetch(`${API_URL}/api/matching/${resultId}/learning`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ save_to_learning: true }),
      }).catch(() => {});

      setEditingId(null);
      setEditValue('');
      setSearchTerm('');
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveQuantity = async (resultId) => {
    const newQty = parseInt(editQuantityValue, 10);
    setEditingQuantityId(null);
    setEditQuantityValue('');
    if (isNaN(newQty) || newQty < 1) return;
    const current = results.find(r => r.id === resultId);
    if (current && current.quantity === newQty) return;

    try {
      const response = await fetch(`${API_URL}/api/matching/${resultId}/quantity`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: newQty }),
      });
      if (response.ok) {
        setResults(results.map(r => r.id === resultId ? { ...r, quantity: newQty } : r));
      }
    } catch (err) {
      // ignore
    }
  };

  // Выбор элемента из dropdown
  const handleSelectItem = (resultId, itemName) => {
    handleSaveEdit(resultId, itemName);
  };

  const handleDeleteMatch = async (resultId) => {
    if (!window.confirm('Вы уверены, что хотите удалить это соответствие?')) {
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/matching/delete/${resultId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          window.location.reload();
          return;
        }
        throw new Error('Не удалось удалить соответствие');
      }

      // Update local state - clear matched_name
      setResults(results.map(r =>
        r.id === resultId
          ? { ...r, matched_name: null, status: 'manual' }
          : r
      ));
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadExcel = () => {
    // Прямая ссылка с токеном в query параметре — обходит блокировку браузера для HTTP
    const url = `${API_URL}/api/matching/export/${orderId}?token=${encodeURIComponent(token)}`;
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `matching_results_${orderId}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadExcelWithArticles = () => {
    const url = `${API_URL}/api/matching/export-with-articles/${orderId}?token=${encodeURIComponent(token)}`;
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `matching_results_articles_${orderId}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleToggleLearning = async (resultId, newValue) => {
    setLearningStates(prev => ({ ...prev, [resultId]: newValue }));
    try {
      await fetch(`${API_URL}/api/matching/${resultId}/learning`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ save_to_learning: newValue }),
      });
    } catch (err) {
      // revert on error
      setLearningStates(prev => ({ ...prev, [resultId]: !newValue }));
    }
  };

  const showCopyNotification = (message) => {
    setCopyNotification(message);
    setTimeout(() => setCopyNotification(null), 3000);
  };

  const handleCopyFor1C = () => {
    // Копируем только сопоставленные позиции: название (и характеристика, если есть) и количество
    const matchedResults = results.filter(r => r.matched_name && (!r.out_of_stock || r.keep_out_of_stock));
    if (matchedResults.length === 0) {
      showCopyNotification('Нет сопоставленных позиций для копирования');
      return;
    }

    // Колонку «Характеристика» показываем не по тому, есть ли она у позиций именно
    // ЭТОГО заказа, а по тому, ведёт ли компания номенклатуру по характеристикам вообще —
    // стандартная 1С-обработка «Загрузка товаров из внешних файлов» вставляет по фиксированным
    // колонкам (Номенклатура | Характеристика | Количество), одинаковым для всех заказов клиента,
    // если у него включены характеристики. Если для этого конкретного заказа характеристик нет —
    // колонка всё равно должна остаться, просто пустой, иначе собьётся позиционная вставка.
    const hasCharacteristics = companyHasCharacteristics;

    const text = matchedResults
      .map(r => {
        if (!hasCharacteristics) {
          return `${r.matched_name}\t${r.quantity}`;
        }
        const name = r.characteristic_name ? r.base_name : r.matched_name;
        return `${name}\t${r.characteristic_name || ''}\t${r.quantity}`;
      })
      .join('\n');

    copyToClipboard(text, `Скопировано ${matchedResults.length} позиций для 1С`, showCopyNotification);
  };

  const handleKeepOutOfStock = async (result, keep) => {
    setResults(list => list.map(item => item.id === result.id ? { ...item, keep_out_of_stock: keep } : item));
    try {
      const response = await fetch(`${API_URL}/api/matching/result/${result.id}/keep-out-of-stock`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ keep }),
      });
      if (!response.ok) throw new Error('Не удалось сохранить выбор');
    } catch (err) {
      setResults(list => list.map(item => item.id === result.id ? { ...item, keep_out_of_stock: !keep } : item));
      showCopyNotification(err.message);
    }
  };

  const handlePromoteAnalog = async (resultId, analogName) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/matching/${resultId}/promote-analog`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ analog_name: analogName }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Не удалось установить аналог основным');
      }
      // Эндпоинт возвращает analogs как простой список названий (так они и хранятся
      // в БД) — БЕЗ цен/остатков. Тихо перезапрашиваем результаты целиком, чтобы
      // получить обогащённые {name, offers} объекты (та же логика, что и при
      // обычной загрузке страницы), а не затирать их голыми строками.
      await loadResults(true);
    } catch (err) {
      showCopyNotification(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRematch = (id, index, event) => {
    setSelectedForRematch(prev => {
      const next = new Set(prev);
      if (event && event.shiftKey && lastCheckedIndexRef.current !== null) {
        const from = Math.min(lastCheckedIndexRef.current, index);
        const to = Math.max(lastCheckedIndexRef.current, index);
        const shouldSelect = !prev.has(id);
        for (let i = from; i <= to; i++) {
          if (filteredResults[i]) {
            if (shouldSelect) next.add(filteredResults[i].id);
            else next.delete(filteredResults[i].id);
          }
        }
      } else {
        if (next.has(id)) next.delete(id); else next.add(id);
      }
      lastCheckedIndexRef.current = index;
      return next;
    });
  };

  const handleSelectAllRematch = (checked) => {
    if (checked) {
      setSelectedForRematch(new Set(filteredResults.map(r => r.id)));
    } else {
      setSelectedForRematch(new Set());
    }
    lastCheckedIndexRef.current = null;
  };

  const handleSelectAllLearning = (checked) => {
    const newStates = {};
    filteredResults.filter(r => r.matched_name).forEach(r => { newStates[r.id] = checked; });
    setLearningStates(prev => ({ ...prev, ...newStates }));
    // Persist all changes to server
    filteredResults.filter(r => r.matched_name).forEach(r => {
      fetch(`${API_URL}/api/matching/${r.id}/learning`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ save_to_learning: checked }),
      }).catch(() => {});
    });
  };

  // Core rematch execution: no notifications / no reset of comments (caller handles).
  // Returns { ok, matched, total, unmatched, error }.
  // groupSelectionsArg: {result_id: group_name} — если задано, бэкенд пропускает фильтрацию
  // и берёт кандидатов из указанной жирной группы для этих позиций.
  // resultIdsOverride: если задан, используем его вместо selectedForRematch (для рематча по группе,
  // который может запускаться независимо от основных чекбоксов).
  const runRematch = async ({ aiComment, groupSelectionsArg, resultIdsOverride }) => {
    const ids = resultIdsOverride ?? Array.from(selectedForRematch);
    if (!ids || ids.length === 0) return { ok: false, skipped: true };
    setRematching(true);
    try {
      const body = {
        result_ids: ids,
        ai_comment: aiComment,
      };
      if (groupSelectionsArg && Object.keys(groupSelectionsArg).length > 0) {
        body.group_selections = groupSelectionsArg;
      }
      const response = await fetch(`${API_URL}/api/matching/rematch/${orderId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error('Ошибка повторного сопоставления');

      const data = await response.json();
      const updatedMap = {};
      data.updated.forEach(u => { updatedMap[u.id] = u; });

      const matchedCount = data.updated.filter(u => u.matched_name).length;
      const unmatchedCount = data.updated.filter(u => !u.matched_name).length;

      setResults(prev => prev.map(r => updatedMap[r.id] ? { ...r, ...updatedMap[r.id] } : r));
      // Чистим основное выделение только если рематч шёл по нему (не по override).
      if (!resultIdsOverride) setSelectedForRematch(new Set());
      setHasSnapshots(data.has_snapshots !== undefined ? !!data.has_snapshots : true);
      // Сбрасываем фильтр на "Все" — иначе пользователь, запустивший пересопоставление
      // с активным фильтром "Не сопоставлено", не видит только что сопоставившиеся
      // позиции (они пропадают из текущего фильтра) и решает, что рематч не сработал.
      setFilterStatus('all');
      return { ok: true, matched: matchedCount, total: data.updated.length, unmatched: unmatchedCount };
    } catch (err) {
      return { ok: false, error: err.message };
    } finally {
      setRematching(false);
    }
  };

  const loadGroupCatalog = async () => {
    if (groupCatalog !== null || groupCatalogLoading) return;
    setGroupCatalogLoading(true);
    setGroupCatalogError('');
    try {
      const response = await fetch(`${API_URL}/api/nomenclature/groups`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Не удалось загрузить список групп');
      const data = await response.json();
      setGroupCatalog(Array.isArray(data) ? data : []);
    } catch (err) {
      setGroupCatalogError(err.message);
      setGroupCatalog([]);
    } finally {
      setGroupCatalogLoading(false);
    }
  };

  const openGroupPicker = () => {
    const initial = {};
    selectedForRematch.forEach(id => { initial[id] = ''; });
    setGroupSelections(initial);
    setModalChecked(new Set());
    setShowGroupPicker(true);
    loadGroupCatalog();
  };

  const closeGroupPicker = () => {
    if (rematching) return;
    setShowGroupPicker(false);
    setModalChecked(new Set());
  };

  const toggleModalChecked = (id) => {
    setModalChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const applyGroupToChecked = (groupName) => {
    if (!groupName || modalChecked.size === 0) return;
    setGroupSelections(prev => {
      const next = { ...prev };
      modalChecked.forEach(id => { next[id] = groupName; });
      return next;
    });
  };

  const runGroupRematch = async () => {
    const filled = {};
    Object.entries(groupSelections).forEach(([id, name]) => {
      if (name && name.trim()) filled[id] = name.trim();
    });
    if (Object.keys(filled).length === 0) {
      setRematchNotification({ type: 'error', text: 'Укажите группу хотя бы для одной позиции' });
      setTimeout(() => setRematchNotification(null), 4000);
      return;
    }
    setRematchNotification(null);
    const aiComment = rematchComment.trim();
    const r = await runRematch({
      aiComment,
      groupSelectionsArg: filled,
      resultIdsOverride: Object.keys(filled).map(Number),
    });
    if (r.ok) {
      setRematchNotification({
        type: 'success',
        text: `Сопоставлено по группам: ${r.matched} из ${r.total}${r.unmatched > 0 ? ` (${r.unmatched} не найдено)` : ''}`,
      });
      setTimeout(() => setRematchNotification(null), 5000);
      setRematchComment('');
      setSelectedForRematch(new Set());
      closeGroupPicker();
    } else if (r.error) {
      setRematchNotification({ type: 'error', text: r.error });
    }
  };

  const handleRevertRematch = async () => {
    if (!hasSnapshots || reverting) return;
    setReverting(true);
    setRematchNotification(null);
    try {
      const response = await fetch(`${API_URL}/api/matching/revert-rematch/${orderId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const errText = response.status === 404
          ? 'Нет сохранённой предыдущей версии'
          : 'Ошибка отката повторного сопоставления';
        throw new Error(errText);
      }

      const data = await response.json();
      const updatedMap = {};
      (data.updated || []).forEach(u => { updatedMap[u.id] = u; });

      setResults(prev => prev.map(r => updatedMap[r.id] ? { ...r, ...updatedMap[r.id] } : r));
      setHasSnapshots(!!data.has_snapshots);
      setFilterStatus('all');
      setRematchNotification({
        type: 'success',
        text: `Откат выполнен: восстановлено ${data.updated.length} позиций${data.has_snapshots ? ' (есть ещё предыдущая версия)' : ''}`
      });
      setTimeout(() => setRematchNotification(null), 5000);
    } catch (err) {
      setRematchNotification({ type: 'error', text: err.message });
    } finally {
      setReverting(false);
    }
  };

  // Core add-items execution: no notifications. Returns { ok, added, matched, error }.
  const runAddItems = async ({ aiComment }) => {
    const text = addItemsText.trim();
    if (!text) return { ok: false, skipped: true };
    setAddingItems(true);
    try {
      const response = await fetch(`${API_URL}/api/matching/add-items/${orderId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text,
          ai_comment: aiComment,
        }),
      });

      if (!response.ok) {
        let detail = 'Ошибка при добавлении позиций';
        try {
          const err = await response.json();
          if (err && err.detail) detail = err.detail;
        } catch (_) { /* ignore */ }
        throw new Error(detail);
      }

      const data = await response.json();
      const added = data.added || [];
      const matchedCount = added.filter(u => u.matched_name).length;

      setResults(prev => [...prev, ...added]);
      if (data.has_snapshots !== undefined) setHasSnapshots(!!data.has_snapshots);
      setAddItemsText('');
      setShowAddItems(false);
      setFilterStatus('all');
      return { ok: true, added: added.length, matched: matchedCount };
    } catch (err) {
      return { ok: false, error: err.message };
    } finally {
      setAddingItems(false);
    }
  };

  // Unified orchestrator: runs rematch + add-items when applicable.
  // Triggered from either button and from Ctrl+Enter in any related text field.
  const handleRunActions = async () => {
    const hasRematch = selectedForRematch.size > 0;
    const hasAddItems = addItemsText.trim().length > 0;
    if (!hasRematch && !hasAddItems) return;
    if (rematching || addingItems) return;

    setRematchNotification(null);
    const aiComment = rematchComment.trim();

    const messages = [];
    let anyError = null;

    if (hasRematch) {
      const r = await runRematch({ aiComment });
      if (r.ok) {
        messages.push(`повторно сопоставлено ${r.matched} из ${r.total}${r.unmatched > 0 ? ` (${r.unmatched} не найдено)` : ''}`);
      } else if (r.error) {
        anyError = r.error;
      }
    }

    if (hasAddItems && !anyError) {
      const a = await runAddItems({ aiComment });
      if (a.ok) {
        messages.push(`добавлено ${a.added}, сопоставлено ${a.matched}`);
      } else if (a.error) {
        anyError = a.error;
      }
    }

    // Clear comment once both actions done successfully
    if (!anyError) {
      setRematchComment('');
    }

    if (anyError) {
      setRematchNotification({ type: 'error', text: anyError });
    } else if (messages.length > 0) {
      setRematchNotification({ type: 'success', text: `Готово: ${messages.join('; ')}` });
      setTimeout(() => setRematchNotification(null), 5000);
    }
  };

  const filteredResults = results.filter((r) => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'matched') return r.matched_name != null;
    if (filterStatus === 'unmatched') return !r.matched_name;
    return r.status === filterStatus;
  });

  // Показываем колонку "Текст из заказа" только если есть данные
  const hasOriginalText = results.some(r => r.original_text);
  // Раньше требовало именно Алесса-style объектов с остатками (r.analogs?.some(object)) —
  // из-за этого колонка "Аналоги" (и кнопка "Сделать основным") вообще не показывалась
  // у обычного (не-Алесса) профиля, где analogs — простой список названий БЕЗ остатков.
  // Это общая функция ядра, не Алесса-специфичная — колонка должна быть видна, если у
  // хоть одной позиции ЕСТЬ аналоги, в любом формате.
  const hasPriceData = results.some(r => r.primary_availability?.length || (r.analogs && r.analogs.length > 0));

  const stats = {
    total: results.length,
    matched: results.filter((r) => r.matched_name).length,
    gpt: results.filter((r) => r.status === 'gpt').length,
    auto: results.filter((r) => r.status === 'auto').length,
    learning: results.filter((r) => r.status === 'learning').length,
    manual: results.filter((r) => r.status === 'manual' && r.matched_name).length,
    unmatched: results.filter((r) => !r.matched_name).length,
    gpt_rejected: results.filter((r) => r.status === 'gpt_rejected').length,
    filtered: results.filter((r) => r.status === 'filtered').length,
  };

  if (loading) {
    return (
      <div className="results-container">
        <div className="loading">Загрузка результатов...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="results-container">
        <div className="error-message">{error}</div>
        <button className="back-btn" onClick={() => onNavigate('dashboard')}>
          Вернуться на главную
        </button>
      </div>
    );
  }

  return (
    <div className="results-container">
      <div className="results-header">
        <h1>Результаты сопоставления</h1>
        <p className="order-id">Заказ #{orderId}</p>
      </div>

      <div className="results-footer results-footer--top">
        <button className="back-btn" onClick={() => onNavigate('dashboard')}>
          ← Вернуться на главную
        </button>
        <button className="new-order-btn" onClick={() => onNavigate('upload')}>
          Новый заказ →
        </button>
      </div>

      <div className="results-stats">
        <div className="stat-item">
          <span className="stat-label">Всего позиций:</span>
          <span className="stat-value">{stats.total}</span>
        </div>
        <div className="stat-item success">
          <span className="stat-label">Сопоставлено:</span>
          <span className="stat-value">{stats.matched}</span>
        </div>
        {isAdmin && stats.gpt > 0 && (
          <div className="stat-item gpt">
            <span className="stat-label">GPT:</span>
            <span className="stat-value">{stats.gpt}</span>
          </div>
        )}
        {isAdmin && stats.auto > 0 && (
          <div className="stat-item auto">
            <span className="stat-label">Авто:</span>
            <span className="stat-value">{stats.auto}</span>
          </div>
        )}
        {isAdmin && stats.learning > 0 && (
          <div className="stat-item learning">
            <span className="stat-label">Обучение:</span>
            <span className="stat-value">{stats.learning}</span>
          </div>
        )}
        {stats.manual > 0 && (
          <div className="stat-item manual">
            <span className="stat-label">Вручную:</span>
            <span className="stat-value">{stats.manual}</span>
          </div>
        )}
        {stats.unmatched > 0 && (
          <div className="stat-item warning">
            <span className="stat-label">Не сопоставлено:</span>
            <span className="stat-value">{stats.unmatched}</span>
          </div>
        )}
      </div>

      <div className="results-actions">
        <div className="filter-buttons">
          <button
            className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
            onClick={() => setFilterStatus('all')}
          >
            Все ({results.length})
          </button>
          <button
            className={`filter-btn ${filterStatus === 'matched' ? 'active' : ''}`}
            onClick={() => setFilterStatus('matched')}
          >
            Сопоставлено ({stats.matched})
          </button>
          {stats.manual > 0 && (
            <button
              className={`filter-btn ${filterStatus === 'manual' ? 'active' : ''}`}
              onClick={() => setFilterStatus('manual')}
            >
              Вручную ({stats.manual})
            </button>
          )}
          {stats.unmatched > 0 && (
            <button
              className={`filter-btn ${filterStatus === 'unmatched' ? 'active' : ''}`}
              onClick={() => setFilterStatus('unmatched')}
            >
              Не сопоставлено ({stats.unmatched})
            </button>
          )}
        </div>

        <div className="export-buttons">
          <button className="download-btn" onClick={handleDownloadExcel}>
            📥 Скачать Excel
          </button>
          <button className="download-articles-btn" onClick={handleDownloadExcelWithArticles}>
            📥 Скачать Excel с артикулами
          </button>
          <button className="copy-1c-btn" onClick={handleCopyFor1C}>
            📋 Скопировать для 1С
          </button>
          {copyNotification && (
            <span className="copy-toast">{copyNotification}</span>
          )}

          {settings && settings.is_enabled && settings.has_api_key && (
            <button className="export-1c-btn" onClick={() => setShow1CModal(true)}>
              📤 Выгрузить в 1С
            </button>
          )}
        </div>
      </div>

      {rawText && (
        <div className="raw-text-section">
          <button
            className="raw-text-toggle-btn"
            onClick={() => setShowRawText(v => !v)}
          >
            {showRawText ? '▲ Скрыть текст заявки' : '▼ Показать текст заявки'}
          </button>
          {showRawText && (
            <pre className="raw-text-content">{rawText}</pre>
          )}
        </div>
      )}

      <div className="results-table-container">
        <table className="results-table">
          <thead>
            <tr>
              <th>
                <div className="rematch-col-header">
                  <input
                    type="checkbox"
                    title="Выбрать все"
                    checked={filteredResults.length > 0 && filteredResults.every(r => selectedForRematch.has(r.id))}
                    onChange={(e) => handleSelectAllRematch(e.target.checked)}
                  />
                  <span
                    className={`rematch-col-hint${rematchHintVisible ? ' rematch-col-hint--active' : ''}`}
                    title="Выберите позиции для повторного сопоставления"
                    onClick={() => setRematchHintVisible(v => !v)}
                  >?</span>
                  {rematchHintVisible && (
                    <div className="rematch-col-hint-popup">Выберите позиции для повторного сопоставления</div>
                  )}
                </div>
              </th>
              <th>№</th>
              {hasOriginalText && <th>Текст из заказа</th>}
              <th>Товар из заказа</th>
              <th>Сопоставленная номенклатура</th>
              {hasPriceData && <th>Аналоги</th>}
              <th>Количество</th>
              <th>Статус</th>
              <th>
                <div className="rematch-col-header">
                  <input
                    type="checkbox"
                    title="Выбрать все для обучения"
                    checked={filteredResults.filter(r => r.matched_name).length > 0 &&
                             filteredResults.filter(r => r.matched_name).every(r => learningStates[r.id])}
                    onChange={(e) => handleSelectAllLearning(e.target.checked)}
                  />
                  <span>Обучение</span>
                </div>
              </th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {filteredResults.map((result, index) => (
              <tr key={result.id} className={`${!result.matched_name ? 'unmatched' : ''} ${result.out_of_stock && !result.keep_out_of_stock ? 'out-of-stock' : ''} ${selectedForRematch.has(result.id) ? 'selected-for-rematch' : ''}`}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedForRematch.has(result.id)}
                    onChange={(e) => handleToggleRematch(result.id, index, e.nativeEvent)}
                  />
                </td>
                <td>{index + 1}</td>
                {hasOriginalText && (
                  <td className="original-text">{result.original_text || ''}</td>
                )}
                <td className="order-name">{result.order_name}</td>
                <td className="matched-name">
                  {editingId === result.id ? (
                    <div className="inline-dropdown" ref={dropdownRef}>
                      <input
                        ref={searchInputRef}
                        type="text"
                        className="inline-search-input"
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setHighlightedIndex(0); }}
                        placeholder="Поиск..."
                        onKeyDown={(e) => {
                          const filtered = filterNomenclature(nomenclatureList, searchTerm || '').slice(0, 30);
                          if (e.key === 'Escape') { e.preventDefault(); handleCancelEdit(); }
                          else if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedIndex(i => Math.min(i + 1, filtered.length - 1)); }
                          else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedIndex(i => Math.max(i - 1, 0)); }
                          else if (e.key === 'Enter') { e.preventDefault(); if (filtered[highlightedIndex]) handleSelectItem(result.id, filtered[highlightedIndex]); }
                        }}
                      />
                      <div className="inline-search-info">
                        {searchTerm && <span>{countMatches(nomenclatureList, searchTerm)} найдено</span>}
                      </div>
                      <div className="inline-dropdown-list">
                        {filterNomenclature(nomenclatureList, searchTerm || '')
                          .slice(0, 30)
                          .map((item, idx) => {
                            const isSelected = result.matched_name === item;
                            const isHighlighted = idx === highlightedIndex;
                            return (
                              <div
                                key={idx}
                                className={`inline-dropdown-item ${isSelected ? 'selected' : ''} ${isHighlighted ? 'highlighted' : ''}`}
                                onClick={() => handleSelectItem(result.id, item)}
                              >
                                {item}
                              </div>
                            );
                          })}
                        {filterNomenclature(nomenclatureList, searchTerm || '').length === 0 && (
                          <div className="inline-dropdown-empty">Ничего не найдено</div>
                        )}
                      </div>
                    </div>
                  ) : result.matched_name ? (
                    <span
                      className="matched-name-clickable"
                      onClick={() => handleEdit(result)}
                      title="Нажмите, чтобы изменить"
                    >
                      {result.characteristic_name ? (
                        <>
                          {result.base_name}
                          <span className="characteristic-badge" title="Характеристика номенклатуры">
                            {result.characteristic_name}
                          </span>
                        </>
                      ) : (
                        result.matched_name
                      )}
                    </span>
                  ) : (
                    <span
                      className="not-matched matched-name-clickable"
                      onClick={() => handleEdit(result)}
                      title="Нажмите, чтобы выбрать"
                    >
                      Не сопоставлено
                    </span>
                  )}
                  {result.matched_name && result.primary_availability?.length > 0 && <StockOffers offers={result.primary_availability} />}
                  {result.matched_name && result.out_of_stock && <div className="stock-warning">
                    <b>Нет в наличии</b>
                    <label><input type="checkbox" checked={!!result.keep_out_of_stock} onChange={e => handleKeepOutOfStock(result, e.target.checked)} /> Оставить в заказе</label>
                  </div>}
                </td>
                {hasPriceData && <td className="analogs-cell">
                  {(result.analogs || []).map((analog, idx) => {
                    const analogName = typeof analog === 'string' ? analog : analog.name;
                    return (
                      <div className="analog-item" key={idx}>
                        <span>{analogName}</span>
                        {typeof analog !== 'string' && <StockOffers offers={analog.offers || []} />}
                        <button
                          type="button"
                          className="promote-analog-btn"
                          disabled={saving}
                          onClick={() => handlePromoteAnalog(result.id, analogName)}
                        >
                          Сделать основным
                        </button>
                      </div>
                    );
                  })}
                  {(!result.analogs || result.analogs.length === 0) && <span className="no-analogs">—</span>}
                </td>}
                <td className="quantity">
                  {editingQuantityId === result.id ? (
                    <input
                      type="number"
                      className="quantity-edit-input"
                      value={editQuantityValue}
                      min="1"
                      onChange={(e) => setEditQuantityValue(e.target.value)}
                      onBlur={() => handleSaveQuantity(result.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveQuantity(result.id);
                        if (e.key === 'Escape') { setEditingQuantityId(null); setEditQuantityValue(''); }
                      }}
                      autoFocus
                    />
                  ) : (
                    <span
                      className="quantity-clickable"
                      onClick={() => { setEditingQuantityId(result.id); setEditQuantityValue(result.quantity.toString()); }}
                      title="Нажмите, чтобы изменить"
                    >
                      {result.quantity}
                    </span>
                  )}
                </td>
                <td>
                  {isAdmin ? (
                    <>
                      {result.status === 'gpt' && (
                        <span className="status-badge gpt" title={result.gpt_reasoning || ''}>
                          GPT {result.gpt_confidence != null ? `${result.gpt_confidence}%` : ''}
                        </span>
                      )}
                      {result.status === 'auto' && (
                        <span className="status-badge auto">Авто</span>
                      )}
                      {result.status === 'learning' && (
                        <span className="status-badge learning">Обучение</span>
                      )}
                      {result.status === 'manual' && (
                        <span className="status-badge manual">Вручную</span>
                      )}
                      {result.status === 'gpt_rejected' && (
                        <span className="status-badge gpt-rejected">
                          GPT ❌
                          {result.gpt_reasoning && (
                            <span
                              className="status-help-icon"
                              onClick={() => alert(result.gpt_reasoning)}
                              title={result.gpt_reasoning}
                            >?</span>
                          )}
                        </span>
                      )}
                      {result.status === 'gpt_error' && (
                        <span className="status-badge gpt-error">
                          GPT недоступен
                          <span
                            className="status-help-icon"
                            onClick={() => alert(result.gpt_reasoning || 'GPT API недоступен — выбор не был сделан')}
                            title={result.gpt_reasoning || 'GPT API недоступен — выбор не был сделан'}
                          >?</span>
                        </span>
                      )}
                      {result.status === 'filtered' && (
                        <span className="status-badge filtered">
                          Отфильтровано
                          <span
                            className="status-help-icon"
                            onClick={() => alert('Все кандидаты отсеяны фильтрами, 0 вариантов')}
                            title="Все кандидаты отсеяны фильтрами, 0 вариантов"
                          >?</span>
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      {(result.status === 'gpt' || result.status === 'auto' || result.status === 'learning') && (
                        <span className="status-badge auto">Авто</span>
                      )}
                      {result.status === 'manual' && (
                        <span className="status-badge manual">Вручную</span>
                      )}
                      {result.status === 'gpt_error' && (
                        <span className="status-badge gpt-error">GPT недоступен</span>
                      )}
                      {(result.status === 'gpt_rejected' || result.status === 'filtered') && (
                        <span className="status-badge warning-badge">Не сопоставлено</span>
                      )}
                    </>
                  )}
                </td>
                <td className="learning-cell">
                  {result.matched_name && (
                    <label className="learning-checkbox">
                      <input
                        type="checkbox"
                        checked={learningStates[result.id] || false}
                        onChange={(e) => handleToggleLearning(result.id, e.target.checked)}
                      />
                      <span className={learningStates[result.id] ? 'learning-saved' : 'learning-not-saved'}>
                        {learningStates[result.id] ? 'Сохранено' : 'Не сохранено'}
                      </span>
                    </label>
                  )}
                </td>
                <td className="actions-cell">
                  <div className="edit-actions">
                    <button
                      className="edit-btn icon-btn"
                      onClick={() => handleEdit(result)}
                      title="Изменить"
                      aria-label="Изменить"
                    >
                      ✎
                    </button>
                    {result.matched_name && (
                      <button
                        className="delete-btn icon-btn"
                        onClick={() => handleDeleteMatch(result.id)}
                        disabled={saving}
                        title="Удалить"
                        aria-label="Удалить"
                      >
                        ✗
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredResults.length === 0 && (
        <div className="no-results">
          <p>Нет результатов для выбранного фильтра</p>
        </div>
      )}

      <div className="add-items-panel">
        {!showAddItems ? (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              className="add-items-toggle-btn"
              onClick={() => setShowAddItems(true)}
              disabled={addingItems}
            >
              ➕ Добавить позиции
            </button>
            {selectedForRematch.size > 0 && (
              <button
                className="add-items-toggle-btn"
                onClick={openGroupPicker}
                disabled={rematching}
                title="Задать группу номенклатуры для выбранных позиций — фильтрация пропускается, сопоставление идёт внутри группы"
              >
                🎯 Указать группу ({selectedForRematch.size})
              </button>
            )}
          </div>
        ) : (
          <div className="add-items-body">
            <div className="add-items-header">
              <span className="add-items-title">Добавить новые позиции к заказу</span>
              <button
                className="add-items-close-btn"
                onClick={() => { setShowAddItems(false); setAddItemsText(''); }}
                disabled={addingItems}
                title="Скрыть"
              >
                ✕
              </button>
            </div>
            <textarea
              className="add-items-textarea"
              placeholder="Вставьте текст / таблицу / список новых позиций (по одной на строку или в табличном виде)"
              value={addItemsText}
              onChange={(e) => setAddItemsText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  handleRunActions();
                }
              }}
              rows={6}
              disabled={addingItems}
            />
            <div className="add-items-hint">
              Комментарий для AI из блока «Повторное сопоставление» ниже тоже применится к этим позициям.
            </div>
          </div>
        )}
      </div>

      <div className="rematch-panel">
        <div className="rematch-panel-header">
          <span className="rematch-title">Повторное сопоставление</span>
          {selectedForRematch.size > 0 && (
            <span className="rematch-count">Выбрано позиций: {selectedForRematch.size}</span>
          )}
        </div>
        <div style={{width: '100%'}}>
          <div style={{fontSize: '13px', color: '#2c3e50', fontWeight: 600, marginBottom: '2px'}}>💬 Комментарий для AI</div>
          <div style={{fontSize: '11px', color: '#888', marginBottom: '4px', lineHeight: '1.3'}}>
            Допишите недостающее в названиях (марки, комплекты) и/или предпочтения при выборе
          </div>
          <textarea
            className="rematch-comment"
            placeholder='Напр.: ко всем сверлам добавь Р6М5К5; предпочитай шлифованные; исключай позиции с "А1"; марку стали ищи в артикуле'
            value={rematchComment}
            onChange={(e) => setRematchComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                handleRunActions();
              }
            }}
            rows={2}
          />
        </div>
        <div className="rematch-bottom">
          {(() => {
            const hasRematch = selectedForRematch.size > 0;
            const hasAddItems = addItemsText.trim().length > 0;
            const busy = rematching || addingItems;
            let label;
            if (busy) {
              label = '⏳ Сопоставление...';
            } else if (hasRematch && hasAddItems) {
              label = `🚀 Запустить сопоставление (повтор ${selectedForRematch.size} + добавить)`;
            } else if (hasRematch) {
              label = `🚀 Запустить сопоставление (${selectedForRematch.size})`;
            } else {
              label = '🚀 Запустить сопоставление';
            }
            return (
              <button
                className="rematch-btn"
                onClick={handleRunActions}
                disabled={(!hasRematch && !hasAddItems) || busy}
                title={
                  hasRematch && hasAddItems
                    ? 'Добавить новые позиции И повторно сопоставить выбранные. Комментарий для AI применится к обоим действиям.'
                    : hasRematch
                      ? 'Повторно сопоставить выбранные позиции с учётом AI-комментариев.'
                      : hasAddItems
                        ? 'Разобрать текст из поля «Добавить позиции» и запустить сопоставление для них. Комментарий для AI применится.'
                        : 'Отметьте позиции галочками или введите текст в «➕ Добавить позиции».'
                }
              >
                {label}
              </button>
            );
          })()}
          {hasSnapshots && (
            <button
              className="rematch-revert-btn"
              onClick={handleRevertRematch}
              disabled={reverting}
              title="Вернуть результаты к состоянию до последнего повторного сопоставления. Ручные правки сохранятся."
            >
              {reverting ? '⏳ Откат...' : '↩️ Назад к предыдущей версии'}
            </button>
          )}
          {selectedForRematch.size === 0 && !rematching && !addItemsText.trim() && (
            <div style={{ marginTop: '6px', fontSize: '13px', color: '#888' }}>
              Отметьте галочками позиции для повторного сопоставления или добавьте новые в поле «➕ Добавить позиции».
            </div>
          )}
          {rematchNotification && (
            <span className={`rematch-notification rematch-notification--${rematchNotification.type}`}>
              {rematchNotification.type === 'success' ? '✓' : '✗'} {rematchNotification.text}
            </span>
          )}
        </div>
      </div>

      <div className="export-buttons export-buttons--bottom">
        <button className="download-btn" onClick={handleDownloadExcel}>
          📥 Скачать Excel
        </button>
        <button className="download-articles-btn" onClick={handleDownloadExcelWithArticles}>
          📥 Скачать Excel с артикулами
        </button>
        <button className="copy-1c-btn" onClick={handleCopyFor1C}>
          📋 Скопировать для 1С
        </button>
        {copyNotification && (
          <span className="copy-toast">{copyNotification}</span>
        )}
        {settings && settings.is_enabled && settings.has_api_key && (
          <button className="export-1c-btn" onClick={() => setShow1CModal(true)}>
            📤 Выгрузить в 1С
          </button>
        )}
      </div>

      <div className="results-footer">
        <button className="back-btn" onClick={() => onNavigate('dashboard')}>
          ← Вернуться на главную
        </button>
        <button className="new-order-btn" onClick={() => onNavigate('upload')}>
          Новый заказ →
        </button>
      </div>

      {/* 1C Export Modal */}
      {show1CModal && (
        <div className="modal-overlay" onClick={() => setShow1CModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Выгрузка заказа в 1С</h2>
              <button className="modal-close" onClick={() => setShow1CModal(false)}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="info-block">
                <p><strong>ID заказа для выгрузки:</strong></p>
                <div className="order-id-display">
                  <code>{orderId}</code>
                  <button
                    className="copy-btn"
                    onClick={() => {
                      copyToClipboard(orderId.toString(), 'ID заказа скопирован в буфер обмена', showCopyNotification);
                    }}
                  >
                    📋 Копировать
                  </button>
                </div>
              </div>

              <div className="info-block">
                <p><strong>Статистика:</strong></p>
                <ul>
                  <li>Всего позиций в заказе: {stats.total}</li>
                  <li>Сопоставлено: {stats.matched}</li>
                  <li>Будет выгружено: {stats.matched} позиций</li>
                </ul>
                {stats.unmatched > 0 && (
                  <p className="warning-text">
                    ⚠️ Несопоставленные позиции ({stats.unmatched}) не будут выгружены
                  </p>
                )}
              </div>

              <div className="info-block instructions">
                <p><strong>Инструкция для создания заказа в 1С:УТ 11:</strong></p>
                <ol>
                  <li>Откройте 1С:Управление торговлей 11</li>
                  <li>Откройте внешнюю обработку <strong>ИмпортЗаказа.epf</strong></li>
                  <li>Выполните функцию с ID заказа: <code>{orderId}</code></li>
                  <li>Заказ будет создан в режиме черновика (без контрагента)</li>
                  <li>Заполните контрагента и проведите документ вручную</li>
                </ol>
                <p className="note">
                  ℹ️ Будет создан документ "Заказ покупателя" с сопоставленными позициями.
                  Контрагент не заполняется автоматически - вы заполните его вручную в 1С.
                </p>
              </div>

              <div className="code-example">
                <p><strong>Код для выполнения в 1С:</strong></p>
                <pre>
{`СоздатьЗаказПокупателяИзСопоставления(${orderId});`}
                </pre>
                <button
                  className="copy-btn"
                  onClick={() => {
                    copyToClipboard(`СоздатьЗаказПокупателяИзСопоставления(${orderId});`, 'Код скопирован в буфер обмена', showCopyNotification);
                  }}
                >
                  📋 Копировать код
                </button>
              </div>
            </div>

            <div className="modal-footer">
              <button className="close-modal-btn" onClick={() => setShow1CModal(false)}>
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {showGroupPicker && (
        <div className="modal-overlay" onClick={closeGroupPicker}>
          <div className="modal-content group-picker-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', width: '90%' }}>
            <div className="modal-header">
              <h2>🎯 Указать группу номенклатуры</h2>
              <button className="modal-close" onClick={closeGroupPicker} disabled={rematching}>✕</button>
            </div>

            <div className="modal-body">
              <p style={{ color: '#555', marginBottom: '12px', fontSize: '13px' }}>
                Для отмеченных позиций фильтрация будет пропущена — сопоставление пойдёт
                напрямую среди всех позиций выбранной группы. Комментарий для AI из блока
                «Повторное сопоставление» применится.
              </p>

              {groupCatalogError && (
                <div style={{ background: '#ffecec', color: '#c00', padding: '8px', borderRadius: '4px', marginBottom: '8px' }}>
                  {groupCatalogError}
                </div>
              )}

              {groupCatalogLoading && (
                <div style={{ color: '#888', marginBottom: '8px' }}>Загрузка списка групп...</div>
              )}

              {groupCatalog !== null && groupCatalog.length === 0 && !groupCatalogLoading && (
                <div style={{ background: '#fff8e1', color: '#7a5a00', padding: '10px', borderRadius: '4px', marginBottom: '10px' }}>
                  Группы не найдены. Загрузите Excel-файл с жирными заголовками групп,
                  либо нажмите «Перезагрузить кеш» в настройках, чтобы восстановить группы
                  по загруженной ранее номенклатуре.
                </div>
              )}

              {/* Bulk-bar — виден только когда в модалке отмечена хотя бы одна строка */}
              {modalChecked.size > 0 && groupCatalog && groupCatalog.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px', padding: '8px', background: '#e3f2fd', borderRadius: '4px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600 }}>
                    Выбрать группу для отмеченных ({modalChecked.size}):
                  </span>
                  <input
                    list="group-catalog-bulk"
                    placeholder="начните вводить название группы"
                    style={{ flex: 1, padding: '6px', border: '1px solid #bbb', borderRadius: '4px' }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        applyGroupToChecked(e.currentTarget.value);
                        e.currentTarget.value = '';
                      }
                    }}
                    onBlur={(e) => {
                      if (e.currentTarget.value) {
                        applyGroupToChecked(e.currentTarget.value);
                        e.currentTarget.value = '';
                      }
                    }}
                  />
                  <datalist id="group-catalog-bulk">
                    {groupCatalog.map(g => (
                      <option key={g.name} value={g.name}>{`${g.name} (${g.count})`}</option>
                    ))}
                  </datalist>
                </div>
              )}

              <div style={{ maxHeight: '50vh', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead style={{ background: '#f5f5f5', position: 'sticky', top: 0 }}>
                    <tr>
                      <th style={{ padding: '8px', width: '36px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={modalChecked.size === Object.keys(groupSelections).length && Object.keys(groupSelections).length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setModalChecked(new Set(Object.keys(groupSelections).map(Number)));
                            } else {
                              setModalChecked(new Set());
                            }
                          }}
                          title="Отметить все"
                        />
                      </th>
                      <th style={{ padding: '8px', width: '40px', textAlign: 'center' }}>№</th>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Товар из заказа</th>
                      <th style={{ padding: '8px', textAlign: 'left', width: '45%' }}>Группа</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(groupSelections).map((rid, idx) => {
                      const id = Number(rid);
                      const result = results.find(r => r.id === id);
                      if (!result) return null;
                      return (
                        <tr key={id} style={{ borderTop: '1px solid #eee' }}>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={modalChecked.has(id)}
                              onChange={() => toggleModalChecked(id)}
                            />
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center', color: '#888' }}>{idx + 1}</td>
                          <td style={{ padding: '8px' }}>{result.order_name}</td>
                          <td style={{ padding: '8px' }}>
                            <input
                              list={`group-catalog-row-${id}`}
                              value={groupSelections[id] || ''}
                              onChange={(e) => setGroupSelections(prev => ({ ...prev, [id]: e.target.value }))}
                              placeholder={groupCatalog && groupCatalog.length > 0 ? 'выберите группу' : '—'}
                              disabled={!groupCatalog || groupCatalog.length === 0}
                              style={{ width: '100%', padding: '6px', border: '1px solid #bbb', borderRadius: '4px' }}
                            />
                            <datalist id={`group-catalog-row-${id}`}>
                              {(groupCatalog || []).map(g => (
                                <option key={g.name} value={g.name}>{`${g.name} (${g.count})`}</option>
                              ))}
                            </datalist>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="modal-footer" style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="close-modal-btn" onClick={closeGroupPicker} disabled={rematching}>
                Отмена
              </button>
              <button
                className="rematch-btn"
                onClick={runGroupRematch}
                disabled={rematching || !groupCatalog || groupCatalog.length === 0}
              >
                {rematching ? '⏳ Сопоставление...' : '🚀 Запустить сопоставление по группам'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default Results;
